import React from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Paper,
    Stack,
    Fade,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Security as SecurityIcon,
    SmartToy as AiIcon,
    Speed as SpeedIcon,
    BugReport as VulnIcon,
    AssuredWorkload as ComplianceIcon,
    Api as ApiIcon,
    Timeline as TimelineIcon,
    AutoGraph as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const LandingPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleStartScan = () => {
        navigate('/login');  // Redirect to login instead of /scan
    };

    const features = [
        {
            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
            title: "AI-Powered Security Scanning",
            description: "Advanced vulnerability detection using machine learning algorithms that evolve with each scan."
        },
        {
            icon: <VulnIcon sx={{ fontSize: 40 }} />,
            title: "Zero-Day Detection",
            description: "Proactively identify unknown vulnerabilities using pattern recognition and behavioral analysis."
        },
        {
            icon: <ComplianceIcon sx={{ fontSize: 40 }} />,
            title: "Compliance Ready",
            description: "Built-in compliance checks for GDPR, PCI DSS, ISO 27001, and other major standards."
        },
        {
            icon: <ApiIcon sx={{ fontSize: 40 }} />,
            title: "API Integration",
            description: "Seamlessly integrate with your existing security tools through our comprehensive API."
        }
    ];

    const benefits = [
        {
            title: "50% Faster Scans",
            description: "Our AI-optimized scanning engine completes thorough security assessments in half the time."
        },
        {
            title: "99.9% Accuracy",
            description: "Industry-leading precision with minimal false positives thanks to AI verification."
        },
        {
            title: "24/7 Monitoring",
            description: "Continuous security assessment with real-time threat detection and alerts."
        }
    ];

    return (
        <Box sx={{ overflow: 'hidden' }}>
            {/* Hero Section */}
            <Box 
                sx={{ 
                    backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                    color: 'white',
                    pt: { xs: 8, md: 12 },
                    pb: { xs: 10, md: 16 }
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Fade in timeout={1000}>
                                <Stack spacing={4}>
                                    <Typography 
                                        variant="h1" 
                                        sx={{ 
                                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                                            fontWeight: 800,
                                            lineHeight: 1.2,
                                            animation: `${pulse} 3s infinite`
                                        }}
                                    >
                                        Next-Gen Web Security with AI
                                    </Typography>
                                    <Typography 
                                        variant="h5" 
                                        sx={{ 
                                            color: 'rgba(255,255,255,0.9)',
                                            lineHeight: 1.6 
                                        }}
                                    >
                                        Protect your web applications with our AI-powered vulnerability scanner. 
                                        Detect threats before they become breaches.
                                    </Typography>
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        spacing={2}
                                    >
                                        <Button 
                                            variant="contained" 
                                            size="large"
                                            color="secondary"
                                            onClick={() => navigate('/scan')}
                                            sx={{ 
                                                py: 1.5,
                                                px: 4,
                                                fontSize: '1.1rem',
                                                animation: `${pulse} 2s infinite`
                                            }}
                                        >
                                            Start Free Scan
                                        </Button>
                                        <Button 
                                            variant="outlined" 
                                            size="large"
                                            sx={{ 
                                                py: 1.5,
                                                px: 4,
                                                fontSize: '1.1rem',
                                                color: 'white',
                                                borderColor: 'white',
                                                '&:hover': {
                                                    borderColor: 'white',
                                                    bgcolor: 'rgba(255,255,255,0.1)'
                                                }
                                            }}
                                            onClick={() => navigate('/plans')}
                                        >
                                            View Plans
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Fade>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {/* Add dashboard preview or security visualization */}
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Container sx={{ py: { xs: 8, md: 12 } }}>
                <Grid container spacing={6}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} md={6} lg={3} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    height: '100%',
                                    bgcolor: 'background.default',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                <Stack spacing={2} alignItems="flex-start">
                                    <Box sx={{ color: 'primary.main' }}>
                                        {feature.icon}
                                    </Box>
                                    <Typography variant="h6" gutterBottom>
                                        {feature.title}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        {feature.description}
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Benefits Section */}
            <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 12 } }}>
                <Container>
                    <Typography 
                        variant="h2" 
                        align="center" 
                        gutterBottom
                        sx={{ mb: 8, fontWeight: 700 }}
                    >
                        Why Choose ExploitFinder?
                    </Typography>
                    <Grid container spacing={4}>
                        {benefits.map((benefit, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Box 
                                    sx={{ 
                                        textAlign: 'center',
                                        p: 3 
                                    }}
                                >
                                    <Typography 
                                        variant="h3" 
                                        color="primary"
                                        gutterBottom
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {benefit.title}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        {benefit.description}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Call to Action */}
            <Box 
                sx={{ 
                    bgcolor: 'primary.main',
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography 
                        variant="h3" 
                        gutterBottom
                        sx={{ fontWeight: 700 }}
                    >
                        Ready to Secure Your Applications?
                    </Typography>
                    <Typography 
                        variant="h6" 
                        sx={{ mb: 4, color: 'rgba(255,255,255,0.9)' }}
                    >
                        Start with a free scan today and discover how our AI-powered security can protect your business.
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        onClick={handleStartScan}
                        sx={{
                            py: 2,
                            px: 6,
                            fontSize: '1.2rem',
                            animation: `${pulse} 2s infinite`
                        }}
                    >
                        Start Scanning Now
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage;
